import * as React from 'react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cityIn } from 'lvovich';

import Link from '../../../Link';
import { getDurationString } from '../../../Results/utils';
import { Duration } from '../../../types';
import Tooltip from '../../../BaseComponents/Tooltip';
import Details from './Details/Details';
import TransferInfo from '../../../TransferInfo';
import AircraftPopup from '../../../AircraftPopup';
import { weatherIcons } from '../../../MobileSegmentInfo/icons';
import { useTheme } from '../../../theme';
import Amenities from '../../../Amenities';
import Point from './Point/Point';
import { SegmentFragment, TemperatureUnits } from '@websky/graphql';
import { useRenderers } from '../../../renderProps';
import ImageWithFallback from '../../../ImageWithFallback';
import { useFlightIconFallbackSrc } from '../../../DesktopFlightRoute/components/Segment/FlightInfo/hooks';
import AircraftLivery from '../../../AircraftLivery/components/AircraftLivery';

const leafIcon = (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6.47561 5.43687C3.25652 7.31002 3.4413 10.361 3.55213 11.4899C7.70996 6.55073 13.9292 6.79026 13.9292 6.79026C13.9292 6.79026 5.11326 9.82287 2.52396 15.9041C2.31948 16.3842 3.48341 17.0085 3.74904 16.441C4.5419 14.7497 5.64671 13.4813 5.64671 13.4813C7.27672 14.0896 10.0964 14.8025 12.095 13.3921C14.7497 11.5187 14.4783 7.3656 18.2679 5.34346C19.1531 4.87133 10.8398 2.89717 6.47561 5.43687Z"
			fill="#41B74C"
		/>
	</svg>
);

const infoIcon = (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M1.33301 8.00004C1.33301 4.32004 4.31967 1.33337 7.99968 1.33337C11.6797 1.33337 14.6663 4.32004 14.6663 8.00004C14.6663 11.68 11.6797 14.6667 7.99968 14.6667C4.31967 14.6667 1.33301 11.68 1.33301 8.00004ZM8.66634 7.33337V11.3334H7.33301V7.33337H8.66634ZM7.99966 13.3334C5.05967 13.3334 2.66633 10.94 2.66633 8.00004C2.66633 5.06004 5.05967 2.66671 7.99966 2.66671C10.9397 2.66671 13.333 5.06004 13.333 8.00004C13.333 10.94 10.9397 13.3334 7.99966 13.3334ZM8.66634 4.6667V6.00004H7.33301V4.6667H8.66634Z"
			fill="#313131"
		/>
	</svg>
);

interface Props {
	segment: SegmentFragment;
	nextSegment?: SegmentFragment;
	transferDuration?: Duration;
	showFlightInfoTooltip?: boolean;
	isReturnFlight?: boolean;
}

const Segment: React.FunctionComponent<Props> = memo(
	({ segment, transferDuration, nextSegment, showFlightInfoTooltip, isReturnFlight }) => {
		const { Segment: css } = useTheme('DesktopFlightInfo');
		const { t } = useTranslation();

		const renderAircraftButton = useCallback(
			(onClick: () => void) => (
				<>
					<Link className={css.aircraft__name__link} action={onClick}>
						{segment.aircraft.name}
					</Link>
					<span className={css.aircraft__name}>{segment.aircraft.name}</span>
				</>
			),
			[segment]
		);

		const renderAircraftPlane = useCallback(
			(onClick: () => void) => (
				<AircraftLivery
					className={css.plane}
					aircraft={segment.aircraft}
					isReturnFlight={isReturnFlight}
					onClick={onClick}
				/>
			),
			[segment]
		);

		const fallbackSrc = useFlightIconFallbackSrc();

		const { renderFlightInfoIcon: RenderFlightInfoIconOverride = ImageWithFallback } = useRenderers();

		return (
			<div>
				<div className={css.segment}>
					<div className={css.points}>
						<Point location={segment.departure} />

						<div className={css.duration}>
							{t('DesktopFlightInfo:Duration')}: {getDurationString(segment.duration)}
							{segment.departure.date !== segment.arrival.date && !segment.stops?.length && (
								<span className={css.nextDayArrival}>
									{t('DesktopFlightInfo:Arrival the next day')}
								</span>
							)}
							{!!segment.stops?.length && (
								<span className={css.nextDayArrival}>
									{t('DesktopFlightInfo:TechnicalLandingIn', {
										count: segment.stops?.length
									})}{' '}
									{segment.stops
										?.map(
											stop =>
												`${cityIn(stop?.airport?.city?.name)}${
													stop?.duration ? ` - ${getDurationString(stop.duration)}` : ''
												}`
										)
										.join(', ')}
								</span>
							)}
						</div>

						<Point location={segment.arrival} />
					</div>

					<div className={css.weather}>
						{segment.departure.weather && (
							<span className={css.weather__row}>
								<span className={css.weather__icon}>
									{weatherIcons[segment.departure.weather.type]
										? weatherIcons[segment.departure.weather.type]
										: null}
								</span>
								<span className={css.weather__temperature}>
									{Math.round(segment.departure.weather.temperature) > 0
										? '+'
										: Math.round(segment.departure.weather.temperature) < 0
										? '-'
										: ''}
									{Math.abs(Math.round(segment.departure.weather.temperature))}&deg;
									{segment.departure.weather.measurement === TemperatureUnits.Fahrenheit && 'F'}
								</span>
							</span>
						)}

						{segment.arrival.weather && (
							<span className={css.weather__row}>
								<span className={css.weather__icon}>
									{weatherIcons[segment.arrival.weather.type]
										? weatherIcons[segment.arrival.weather.type]
										: null}
								</span>
								<span className={css.weather__temperature}>
									{Math.round(segment.arrival.weather.temperature) > 0
										? '+'
										: Math.round(segment.arrival.weather.temperature) < 0
										? '-'
										: ''}
									{Math.abs(Math.round(segment.arrival.weather.temperature))}&deg;
									{segment.arrival.weather.measurement === TemperatureUnits.Fahrenheit && 'F'}
								</span>
							</span>
						)}
					</div>

					<div className={css.flight}>
						<div className={css.airline}>
							{segment.operatingAirline.icon && (
								<span className={css.logo}>
									<RenderFlightInfoIconOverride
										src={segment.operatingAirline.icon}
										fallbackSrc={fallbackSrc}
										alt={segment.operatingAirline?.name}
									/>
								</span>
							)}
							{segment.operatingAirline.name} • {segment.marketingAirline.iata}
							{'-'}
							{segment.flightNumber}{' '}
							{segment.flightInfo && segment.flightInfo.markers.eco && (
								<Tooltip title={t('Eco-friendly')}>
									<span className={css.flight__ecoIcon}>{leafIcon}</span>
								</Tooltip>
							)}
							{segment.flightInfo && showFlightInfoTooltip && !!segment.flightInfo.statistics.length && (
								<Tooltip
									title={
										<Details
											items={segment.flightInfo.statistics.map(item => ({
												label: item.title,
												value: item.value
											}))}
										/>
									}
								>
									<span className={css.flight__infoIcon}>{infoIcon}</span>
								</Tooltip>
							)}
						</div>

						<div>
							{segment.aircraft?.id && (
								<AircraftPopup
									id={segment.aircraft.id}
									operatingIata={segment.operatingAirline.iata}
									name={segment.aircraft.name}
									renderButton={renderAircraftButton}
								/>
							)}
						</div>

						{segment.flightInfo?.amenities?.length > 0 && (
							<Amenities className={css.amenities} amenities={segment.flightInfo.amenities} />
						)}
					</div>

					{segment.aircraft?.id && (
						<AircraftPopup
							id={segment.aircraft.id}
							operatingIata={segment.operatingAirline.iata}
							name={segment.aircraft.name}
							renderButton={renderAircraftPlane}
						/>
					)}
				</div>

				{transferDuration && nextSegment && (
					<TransferInfo
						className={css.transfer}
						segment={segment}
						nextSegment={nextSegment}
						transferDuration={transferDuration}
					/>
				)}
			</div>
		);
	}
);

export default Segment;
